import { FormCreateTicketControl } from 'tickets/actions2/common/action.types';

import { FieldMetaDataType } from './action-constants';

export enum FieldName {
    TicketId = '__ticket_id__',
    AgencyId = '__ticket_agencyID__',
    TenantId = '__ticket_tenantID__',
    ObjectId = '__ticket_object_id__',
    StageId = '__ticket_stageID__',
    Source = '__ticket_source__',
    Sentiment = '__ticket_sentiment__',
    PostReviewResponse = '__ticket_post_review_response_action_filter__',
    ResponseStatus = '__ticket_response_status__',
    LocationId = '__ticket_location_id__',
    LocationUid = '__ticket_location_uid__',
    TicketType = '__ticket_ticketType__',
    Level = '__ticket_level__',
    AssignedTo = '__ticket_assignedTo__',
    AssignedDate = '__ticket_assigned_date__',
    Date = 'date',
    CreatedDate = '__ticket_createdDate__',
    ReviewDate = '__ticket_review_date__',
    ClosedDate = '__ticket_closed_date__',
    DueDate = '__ticket_dueDate__',
    Queue = '__ticket_queueID__',
    QueueName = '__ticket_queue_name__',
    Tag = '__ticket_tagID__',
    TagName = '__ticket_tag_name__',
    Tags = '__ticket_tags__',
    TicketsResponded = '__ticket_responded__',
    TicketsPendingApproval = '__ticket_pending_approval__',
    RootCauses = '__ticket_root_causes__',
    RootCauseId = '__ticket_root_cause_id__',
    RootCauseIds = '__ticket_root_cause_ids__',
    UpdatedDate = '__ticket_updatedDate__',
}

export enum ContactFieldName {
    ContactLocationUid = '__contact_location_uids__',
    ContactTicketLevel = '__contact_ticket_level__',
}

export interface StageItem {
    label: string;
    selected: boolean;
    value: string;
}

export interface Note {
    createdDate?: number;
    id?: string | null;
    createdBy?: number;
    internal: boolean;
    objectID: string;
    comment: string;
    commentHTML?: string;
    agencyId: number;
    locationUid: string;
}

export interface Ticket {
    id: number;
    createdDate: number;
    locationUid: number;
    locationID: string;
    objectID?: string;
    ticketType: number;
    agencyID?: number;
    tenantID: number;
    comment?: string;
    dueDate: number;
    assignedToUser?: string;
    userDefined?: any;
    stageID?: string;
    updatedDate?: number;
}

export enum UserFieldName {
    ActionId = '__user_actionID__',
    Active = '__user_active__',
    AgencyId = '__user_agencyID__',
    Color = '__user_color__',
    DisplayName = '__user_displayName__',
    Email = '__user_email__',
    FirstName = '__user_firstName__',
    LastName = '__user_lastName__',
    LocationUids = '__user_locationUIDs__',
    RoleIds = '__user_roleIDs__',
    Search = '__user_search__',
    System = '__user_system__',
    TenantId = '__user_tenantID__',
    UserId = '__user_userID__',
}

export enum Level {
    Location = 0,
    Tenant = 1,
}

export enum TicketLevel {
    Tenant = 'Tenant',
    Agency = 'Agency',
}

export enum FormControl {
    Title = 'title',
    Name = 'name',
    Email = 'email', // TODO
    Phone = 'phone',
    LocationUid = 'locationUid',
}

export interface FormInputs {
    [FormControl.Title]?: string;
    [FormControl.Name]: string;
    [FormControl.Email]: string;
    [FormControl.Phone]?: string;
    [FormControl.LocationUid]?: string;
}

export interface File {
    id: string;
    fileName: string;
    isImage: boolean;
    url: string;
    relativeURL: string;
    selected: boolean | undefined;
    extension: string;
}

export enum DataType {
    User = 'user',
    Role = 'role',
    Email = 'email',
}

export interface RecipientOptionData {
    /** text to be displayed in the search dropdown */
    displayName: string;
    _data?: any;
}

export const TenantActionFilterNames = {
    ActionId: '__user_actionID__',
    Active: '__user_active__',
    AgencyId: '__user_agencyID__',
    Color: '__user_color__',
    DisplayName: '__user_displayName__',
    Email: '__user_email__',
    FirstName: '__user_firstName__',
    Id: '_id',
    Image: '__user_image__',
    LastName: '__user_lastName__',
    LocationUids: '__user_locationUIDs__',
    Manage: '__user_manage__',
    MiddleName: '__user_middleName__',
    RoleIds: '__user_roleIDs__',
    Search: '__user_search__',
    System: '__user_system__',
    TenantId: '__user_tenantID__',
    Token: 'token',
    UserId: '__user_userID__',
    Deleted: '__user_deleted__',
};

export type CredentialRequest = {
    agencyId: any;
    tenantId: number;
    sourceId: string;
    locationUid: number;
};

export const TicketFieldMapWithAccessor: { [key: string]: string } = {
    [FieldName.TicketId]: 'id',
    [FieldName.Source]: 'sourceName',
    [FieldName.LocationId]: 'locationID',
    [FieldName.LocationUid]: 'locationUid',
    __ticket_full_comment__: 'comment',
    [FieldName.Queue]: 'queueID',
    [FieldName.StageId]: 'stageID',
    [FieldName.Tags]: 'tags',
    [FieldName.Sentiment]: 'userDefined.sentiment',
    [FieldName.DueDate]: 'dueDate',
    [FieldName.CreatedDate]: 'createdDate',
    [FieldName.ClosedDate]: 'closedDate',
    [FieldName.UpdatedDate]: 'updatedDate',
    __ticket_definitionID__: 'ticketDefinitionID',
    __ticket_reporter_name__: 'reporterName',
    __ticket_reporter_email__: 'reporterEmail',
    [FieldName.RootCauses]: 'rootCauses',
    __ticket_closing_reason_id__: 'closedExplanation',
    [FieldName.TicketType]: 'type.label',
};

export const DashboardColumns: { [key: string]: any } = {
    ticketData: {
        label: 'COLUMN_HEADER_TICKET_DATA',
        columns: [
            { label: 'COLUMN_TICKET_ID', value: '__ticket_id__', dataKey: 'id', sortable: true, default: true },
            {
                label: 'COLUMN_SOURCE',
                value: '__ticket_source__',
                dataKey: 'sourceName',
                sortable: true,
                default: true,
                width: 115,
            },
            {
                label: 'COLUMN_LOCATION',
                value: '__ticket_public_location_name__',
                dataKey: 'locationName',
                sortable: true,
                default: true,
                width: 150,
            },
            {
                label: 'COLUMN_SUBJECT',
                value: '__ticket_full_comment__',
                dataKey: 'displayComment',
                sortable: false,
                width: 180,
                default: true,
            },
            {
                label: 'COLUMN_QUEUE',
                value: '__ticket_queueID__',
                dataKey: 'queue.name',
                sortable: false,
                default: true,
                width: 120,
            },
            {
                label: 'COLUMN_STAGE',
                value: '__ticket_stageID__',
                dataKey: 'stage.name',
                sortable: false,
                default: true,
                width: 120,
            },
            {
                label: 'COLUMN_TICKET_TAGS',
                value: '__ticket_tags__',
                sortable: false,
                dataKey: 'tagsString',
                default: true,
            },
            {
                label: 'COLUMN_SYSTEM_TAGS',
                value: '__system_tags__',
                dataKey: 'systemTags',
                sortable: false,
                exportOnly: true,
            },
            {
                label: 'COLUMN_CREATED_BY',
                value: '__ticket_createdBy__',
                dataKey: 'createdByUser',
                sortable: false,
                width: 120,
            },
            { label: 'COLUMN_ASSIGNED_BY', value: '__ticket_assignedBy__', dataKey: 'assignedByUser', width: 120 },
            { label: 'COLUMN_ASSIGNED_TO', value: '__ticket_assignedTo__', dataKey: 'assignedToUser', width: 120 },
            {
                label: 'COLUMN_CLOSED_BY',
                value: '__ticket_closed_by__',
                dataKey: 'closedByUser',
                sortable: false,
                width: 120,
            },
            {
                label: 'COLUMN_SENTIMENT',
                value: '__ticket_sentiment__',
                dataKey: 'userDefined.sentiment',
                sortable: true,
                default: true,
                width: 120,
            },
            {
                label: 'COLUMN_FULFILLMENT_TAG',
                value: '__ticket_bl_fulfillment_tag__',
                dataKey: 'userDefined.blft',
                sortable: false,
            },
            {
                label: 'COLUMN_TICKET_TYPE',
                value: '__ticket_definitionID__',
                dataKey: 'ticketDefinitionID',
                sortable: true,
                width: 160,
            },
            { label: 'COLUMN_NOTE_COUNT', value: '__ticket_notes_count__', dataKey: 'notesCount', sortable: false },
            { label: 'COLUMN_ROOT_CAUSES', value: '__ticket_root_causes__', dataKey: 'rootCauses', sortable: true },
            {
                label: 'COLUMN_CLOSING_REASON',
                value: '__ticket_closing_reason_id__',
                dataKey: 'closedExplanation',
                sortable: true,
            },
            {
                label: 'COLUMN_CHANNEL',
                value: '__ticket_ticketType__',
                dataKey: 'type.label',
                sortable: true,
                width: 105,
            },

            { label: 'COLUMN_RESPONDED', value: '__ticket_responded__', dataKey: 'ticketResponded', exportOnly: true },
            {
                label: 'COLUMN_CLOSED_ON_TIME',
                value: '__ticket_closed_on_time__',
                dataKey: 'ticketClosedOnTime',
                exportOnly: true,
            },
            { label: 'COLUMN_REOPENED', value: '__ticket_reopened__', dataKey: 'ticketReopened', exportOnly: true },
            {
                label: 'COLUMN_HOURS_OPEN',
                value: '__ticket_hours_open__',
                dataKey: 'ticketHoursOpen',
                exportOnly: true,
            },
            {
                label: 'COLUMN_HOURS_TO_CLOSE',
                value: '__ticket_hours_to_close__',
                dataKey: 'ticketHoursToClose',
                exportOnly: true,
            },
            { label: 'COLUMN_NOTES', value: '__ticket_notes__', exportOnly: false, dataKey: 'notes' },
            {
                label: 'COLUMN_TICKET_TENANT',
                value: '__ticket_tenantID__',
                dataKey: 'tenantName',
                sortable: true,
                default: false,
                hideFromColumnSelect: true,
            },
            {
                label: 'COLUMN_SURVEY_TEMPLATE_RESULT_ID',
                value: '__ticket_survey_result_id__',
                dataKey: 'surveyResultID',
                exportOnly: true,
            },
            { label: 'COLUMN_TICKET_NPS', value: '__ticket_nps__', dataKey: 'nps', sortable: false, default: false },
            {
                label: 'COLUMN_TICKET_SURVEY_TEMPLATE',
                value: '__survey_template_id__',
                dataKey: 'surveyTemplateName',
                default: false,
                sortable: false,
            },
            { label: 'COLUMN_RATING', value: '__ticket_rating__', dataKey: 'userDefined.rating', sortable: false },
        ],
    },
    userData: {
        label: 'COLUMN_HEADER_CONTACT_INFORMATION',
        columns: [
            {
                label: 'COLUMN_REPORTER_NAME',
                value: '__ticket_reporter_name__',
                dataKey: 'reporterName',
                sortable: true,
            },
            {
                label: 'COLUMN_REPORTER_EMAIL',
                value: '__ticket_reporter_email__',
                dataKey: 'reporterEmail',
                sortable: true,
            },
            {
                label: 'COLUMN_REPORTER_PHONE',
                value: '__ticket_reporter_phone__',
                dataKey: 'ticketReporterPhone',
                exportOnly: true,
            },
        ],
    },
    locationData: {
        label: 'COLUMN_HEADER_LOCATION_DATA',
        columns: [
            {
                label: 'COLUMN_INTERNAL_LOCATION',
                value: '__ticket_internal_location_name__',
                dataKey: 'internalLocationName',
                sortable: false,
            },
            {
                label: 'COLUMN_LOCATION_UID',
                value: '__ticket_location_uid__',
                dataKey: 'locationUid',
                sortable: false,
            },
            {
                label: 'COLUMN_LOCATION_CODE',
                value: '__ticket_location_code__',
                dataKey: 'locationCode',
                sortable: false,
            },
            {
                label: 'COLUMN_LOCATION_LOCALITY',
                value: '__ticket_location_locality__',
                dataKey: 'locality',
                sortable: false,
            },
            {
                label: 'COLUMN_LOCATION_REGION',
                value: '__ticket_location_region__',
                dataKey: 'region',
                sortable: false,
            },
            {
                label: 'COLUMN_LOCATION_COUNTRY',
                value: '__ticket_location_country__',
                dataKey: 'country',
                sortable: false,
            },
            {
                label: 'COLUMN_LOCATION_ADDRESS',
                value: '__ticket_location_address__',
                dataKey: 'address',
                sortable: false,
            },
        ],
    },
    dates: {
        label: 'COLUMN_HEADER_DATES',
        columns: [
            {
                label: 'COLUMN_DUEDATE',
                value: '__ticket_dueDate__',
                dataKey: 'dueDateNumber',
                sortable: true,
                default: true,
                width: 120,
            },
            {
                label: 'COLUMN_CREATED_DATE',
                value: '__ticket_createdDate__',
                dataKey: 'createdDate',
                sortable: true,
            },
            {
                label: 'COLUMN_CLOSED_DATE',
                value: '__ticket_closed_date__',
                dataKey: 'closedDate',
                sortable: true,
            },
            {
                label: 'COLUMN_LAST_UPDATED_DATE',
                value: '__ticket_updatedDate__',
                dataKey: 'relativeUpdatedDateNumber',
                sortable: true,
                default: true,
                width: 120,
            },
            {
                label: 'COLUMN_TICKET_AGE',
                value: '__ticket_age__',
                dataKey: 'ticketAge',
                sortable: false,
                default: false,
                width: 120,
            },
            { label: 'COLUMN_REOPENED_DATE', value: '__ticket_reopened_date__', exportOnly: true },
        ],
    },
};

export const DefaultDashboardColumns = [
    FieldName.TicketId,
    FieldName.LocationUid,
    '__ticket_full_comment__',
    FieldName.Queue,
    FieldName.StageId,
    FieldName.Tags,
    FieldName.Sentiment,
    FieldName.DueDate,
    FieldName.UpdatedDate,
];

export const FileTypes = {
    imageTypes: ['jpg', 'jpeg', 'png', 'gif'],
    videoTypes: ['.mov', '.mp4'],
    audioTypes: ['.mp3', '.mpg'],
    textTypes: ['.doc', '.docx', '.txt'],
};

export const FilterNames = {
    ReporterEmail: '__ticket_reporter_email__',
    ReporterPhone: '__ticket_reporter_phone__',
    ReporterName: '__ticket_reporter_name__',
    Comment: '__ticket_comment__',
    Id: '__ticket_id__',
};

export const coreCustomTypes: Record<
    FieldMetaDataType,
    {
        label: string;
        name: string;
        renderer: string[];
    }
> = {
    [FieldMetaDataType.Boolean]: {
        label: 'Boolean',
        name: 'booleanValue',
        renderer: ['CheckBox', 'RadioButton'],
    },
    [FieldMetaDataType.DateTime]: {
        label: 'DateTime',
        name: 'dateTimeValue',
        renderer: ['DateTime'],
    },
    [FieldMetaDataType.Double]: {
        label: 'Double',
        name: 'doubleValue',
        renderer: ['Text', 'TextArea', 'TextBox'],
    },
    [FieldMetaDataType.Integer]: {
        label: 'Integer',
        name: 'integerValue',
        renderer: ['Text', 'TextArea', 'TextBox'],
    },
    [FieldMetaDataType.String]: {
        label: 'String',
        name: 'stringValue',
        renderer: ['Text', 'TextArea', 'TextBox', 'Dropdown'],
    },
    [FieldMetaDataType.CalculatedDouble]: {
        label: 'Calculated Double',
        name: 'doubleValue',
        renderer: ['TextBox'],
    },
    [FieldMetaDataType.CalculatedString]: {
        label: 'Calculated String',
        name: 'stringValue',
        renderer: ['TextBox'],
    },
};

export const AppellationOptions = {
    Miss: {
        label: 'APPELLATION_MISS',
        value: 0,
    },
    Mr: {
        label: 'APPELLATION_MR',
        value: 1,
    },
    Mrs: {
        label: 'APPELLATION_MRS',
        value: 2,
    },
    Ms: {
        label: 'APPELLATION_MS',
        value: 3,
    },
    Dr: {
        label: 'APPELLATION_DR',
        value: 4,
    },
    Sir: {
        label: 'APPELLATION_SIR',
        value: 5,
    },
    Mx: {
        label: 'APPELLATION_MX',
        value: 6,
    },
    Dame: {
        label: 'APPELLATION_DAME',
        value: 7,
    },
};

export const TicketAddressTemplatesNameToSkip = new Set(['latitude', 'longitude']);

export const OneRowLocationFields = new Set<string>([
    FormCreateTicketControl.Country,
    FormCreateTicketControl.PostalCode,
    FormCreateTicketControl.Region,
]);

export const MaxDueIn = 10_000;

export const EmailRegexp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const NumberRegexp = /^\d+$/;

export const ReporterNameRegexp = /^[a-zA-Z\d\-_.,'\s]+$/;

export const DecimalRegexp = /^-?\d+(\.\d+)?$/;

export const SkipCloseStage = 'Closed';

export const AssignQueueValue = 'queue';

export const AssignUserValue = 'user';

export const CreateTicketArea = 'create-ticket';

export const MATCH_TICKETS_AREA = 'match-tickets-area';

export const ASSIGN_TO_REPLACE = 'AssignedTo';
